import React from "react";

/**
 * SVG illustration bank for empty states (Safe Zap v2.0).
 * Linha fina, paleta de tokens (brand-500 + bg/border/ink), 120×120.
 * stroke="currentColor" para herdar cor; o uso real preenche com tokens.
 */

const wrap = (children) => (
  <svg
    viewBox="0 0 120 120"
    width="120"
    height="120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="ill-base" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--bg-3, #1a1f22)" />
        <stop offset="100%" stopColor="var(--bg-2, #131618)" />
      </linearGradient>
    </defs>
    {children}
  </svg>
);

export const ILLUSTRATIONS = {
  // Inbox vazio (sem contatos / sem conversas)
  inbox: wrap(
    <>
      <circle cx="60" cy="62" r="42" fill="url(#ill-base)" stroke="var(--border)" />
      <path
        d="M30 58h18l4 8h16l4-8h18v22a8 8 0 0 1-8 8H38a8 8 0 0 1-8-8V58Z"
        fill="var(--bg-1)"
        stroke="var(--brand-500)"
        strokeWidth="1.6"
      />
      <path d="M36 58 46 38h28l10 20" stroke="var(--brand-500)" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="86" cy="40" r="6" fill="var(--brand-500)" opacity=".18" />
      <circle cx="86" cy="40" r="3" fill="var(--brand-500)" />
    </>
  ),

  // Chat vazio (sem mensagens)
  chat: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <rect x="26" y="36" width="50" height="32" rx="8" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <path d="M40 68 36 78l12-10" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" strokeLinejoin="round" />
      <rect x="44" y="56" width="46" height="28" rx="8" fill="var(--bg-2)" stroke="var(--ink-4)" strokeWidth="1.4" />
      <circle cx="56" cy="70" r="2" fill="var(--ink-3)" />
      <circle cx="66" cy="70" r="2" fill="var(--ink-3)" />
      <circle cx="76" cy="70" r="2" fill="var(--ink-3)" />
    </>
  ),

  // Funil / etapas
  funnel: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <path
        d="M30 36h60l-22 26v22l-16-6V62L30 36Z"
        fill="var(--bg-1)"
        stroke="var(--brand-500)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M40 44h40" stroke="var(--ink-4)" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M48 52h24" stroke="var(--ink-4)" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),

  // Bot / chatbot
  bot: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <path d="M60 28v8" stroke="var(--brand-500)" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="60" cy="26" r="3" fill="var(--brand-500)" />
      <rect x="32" y="40" width="56" height="40" rx="10" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <circle cx="48" cy="58" r="4" fill="var(--brand-500)" />
      <circle cx="72" cy="58" r="4" fill="var(--brand-500)" />
      <path d="M50 70h20" stroke="var(--ink-3)" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="42" y="80" width="36" height="10" rx="3" fill="var(--bg-2)" stroke="var(--ink-4)" strokeWidth="1.2" />
    </>
  ),

  // Automação / fluxo
  flow: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <rect x="28" y="42" width="22" height="14" rx="3" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <rect x="70" y="42" width="22" height="14" rx="3" fill="var(--bg-1)" stroke="var(--ink-4)" strokeWidth="1.4" />
      <rect x="49" y="68" width="22" height="14" rx="3" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <path d="M50 49h20" stroke="var(--ink-4)" strokeWidth="1.4" strokeDasharray="2 3" />
      <path d="M39 56v6c0 3 2 6 6 6h6" stroke="var(--brand-500)" strokeWidth="1.6" fill="none" />
      <path d="M81 56v6c0 3-2 6-6 6h-6" stroke="var(--ink-4)" strokeWidth="1.4" fill="none" />
    </>
  ),

  // Tickets
  ticket: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <path
        d="M28 50a4 4 0 0 1 4-4h56a4 4 0 0 1 4 4v8a4 4 0 0 0 0 8v8a4 4 0 0 1-4 4H32a4 4 0 0 1-4-4v-8a4 4 0 0 0 0-8v-8Z"
        fill="var(--bg-1)"
        stroke="var(--brand-500)"
        strokeWidth="1.6"
      />
      <path d="M60 46v32" stroke="var(--ink-4)" strokeWidth="1.4" strokeDasharray="2 3" />
      <circle cx="44" cy="62" r="2" fill="var(--brand-500)" />
      <circle cx="76" cy="62" r="2" fill="var(--ink-3)" />
    </>
  ),

  // Equipe / pessoas
  team: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <circle cx="44" cy="50" r="9" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <circle cx="76" cy="50" r="9" fill="var(--bg-1)" stroke="var(--ink-4)" strokeWidth="1.4" />
      <path
        d="M28 84c0-9 7-16 16-16s16 7 16 16"
        fill="var(--bg-1)"
        stroke="var(--brand-500)"
        strokeWidth="1.6"
      />
      <path
        d="M60 84c0-9 7-16 16-16s16 7 16 16"
        fill="var(--bg-1)"
        stroke="var(--ink-4)"
        strokeWidth="1.4"
      />
    </>
  ),

  // Search / nada encontrado
  search: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <circle cx="54" cy="54" r="18" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.8" />
      <path d="M68 68l14 14" stroke="var(--brand-500)" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M46 54h16" stroke="var(--ink-4)" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),

  // Relatórios / chart
  chart: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <rect x="28" y="34" width="64" height="52" rx="6" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <path d="M36 76l12-14 10 8 14-20 12 14" stroke="var(--brand-500)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="48" cy="62" r="2" fill="var(--brand-500)" />
      <circle cx="58" cy="70" r="2" fill="var(--brand-500)" />
      <circle cx="72" cy="50" r="2" fill="var(--brand-500)" />
      <path d="M36 80h48" stroke="var(--ink-4)" strokeWidth="1.2" />
    </>
  ),

  // WhatsApp / número
  whatsapp: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <path
        d="M60 30c-16 0-29 13-29 29 0 5 1 10 4 14l-3 11 11-3c4 2 9 4 17 4 16 0 29-13 29-29S76 30 60 30Z"
        fill="var(--bg-1)"
        stroke="var(--brand-500)"
        strokeWidth="1.8"
      />
      <path
        d="M50 50c0 14 11 22 22 22 2 0 4-1 5-2l-4-6-7 2c-3-2-6-5-8-8l2-7-6-4c-1 1-4 2-4 3Z"
        fill="var(--brand-500)"
        opacity=".25"
      />
    </>
  ),

  // Star / avaliações
  star: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <path
        d="m60 32 9 18 20 3-15 14 4 20-18-10-18 10 4-20-15-14 20-3 9-18Z"
        fill="var(--bg-1)"
        stroke="var(--brand-500)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </>
  ),

  // Genérico
  generic: wrap(
    <>
      <circle cx="60" cy="60" r="44" fill="url(#ill-base)" stroke="var(--border)" />
      <rect x="36" y="42" width="48" height="36" rx="6" fill="var(--bg-1)" stroke="var(--brand-500)" strokeWidth="1.6" />
      <path d="M44 56h32M44 64h22" stroke="var(--ink-4)" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
};

export function EmptyIllustration({ name = "generic", className = "" }) {
  const svg = ILLUSTRATIONS[name] || ILLUSTRATIONS.generic;
  return <div className={`uiEmptyIllustration ${className}`}>{svg}</div>;
}
